"use client";

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import type { Lot } from "@/app/page";

const CATEGORY_FILTERS = ["Close Outs", "Sneakers", "Apparel"];

const usd = (n: number) => "$" + Math.round(n).toLocaleString();

function Ico({ d, className }: { d: string; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}
const PIN = "M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z M12 10.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z";
const TRUCK = "M14 18V6H2v12h1a2 2 0 1 0 4 0h4M14 8h4l4 5v5h-2a2 2 0 1 1-4 0h-1";
const TAG = "M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0l-7.2-7.2A2 2 0 0 1 2.8 12V4h8a2 2 0 0 1 1.4.6l8.4 8.4a2 2 0 0 1 0 2.8Z M7 7h.01";
const BOX = "M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z M3.3 7 12 12l8.7-5 M12 22V12";
const COIN = "M12 2v20 M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6";

// One logistics/economics chip (icon + label) in the card's detail row.
function Chip({ d, children, tone = "" }: { d: string; children: React.ReactNode; tone?: string }) {
  return (
    <span className={`flex items-center gap-1 ${tone || "text-gray-400"}`}>
      <Ico d={d} className="w-3 h-3 shrink-0" />
      {children}
    </span>
  );
}

// Lot card (C4).
function LotCard({ lot }: { lot: Lot }) {
  const moq = lot.min_order_units || 1;
  const moqPrice = moq * lot.cost_price;
  const takeAllPrice = lot.units_available * lot.cost_price;
  const shipText =
    lot.ship_time || (lot.freight_included ? "Freight included" : "Plus freight");
  const vsMsrp =
    lot.msrp && lot.cost_price
      ? Math.round(((lot.msrp - lot.cost_price) / lot.msrp) * 100)
      : 0;
  const badge =
    lot.status === "live"
      ? { label: "LIVE", color: "#22c55e" }
      : lot.status === "pending"
      ? { label: "UPCOMING", color: "#3b82f6" }
      : { label: "SOLD OUT", color: "#6b7280" };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="group relative rounded-2xl overflow-hidden bg-[#111] border border-white/5 hover:border-white/15 transition-colors"
    >
      {/* Live lots deep-link into the feed at their anchor (same target the
          hero tiles use); the overlay covers the whole card so it reads as a
          single clickable surface. */}
      {lot.status === "live" && (
        <a
          href={`/feed#lot-${lot.id}`}
          aria-label={`View ${lot.title} in the feed`}
          className="absolute inset-0 z-10"
        />
      )}
      <div className="relative h-52 overflow-hidden bg-[#0d0d0d]">
        {/* Product shots vary in aspect ratio: tall apparel model shots, wide
            sneaker stills. Fill the frame with a blurred copy so nothing is
            letterboxed, then show the full image contained on top so a tall
            garment is never cropped down to a headless torso. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={lot.image_url || ""}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-50"
        />
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={lot.image_url || ""}
          alt={lot.title}
          className="relative w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute top-3 left-3 flex gap-2">
          {lot.status === "live" ? (
            // Live lots pulse to read as "selling right now".
            <span
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold text-white shadow-[0_0_12px_rgba(34,197,94,0.7)]"
              style={{ backgroundColor: badge.color }}
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
              </span>
              {badge.label}
            </span>
          ) : (
            <span
              className="px-2.5 py-1 rounded-full text-[10px] font-bold text-white"
              style={{ backgroundColor: badge.color }}
            >
              {badge.label}
            </span>
          )}
          {lot.is_hot && (
            <span className="flex items-center gap-1 bg-orange-500 text-white text-[10px] font-bold px-2 py-1 rounded-full">
              <svg
                className="w-2.5 h-2.5"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M12 2c1 3 4 4 4 8a4 4 0 0 1-8 0c0-1 .5-2 1-3 .5 2 2 2 3 3 .5-2-1-4 0-6-1 0-3 1-4 3-1-3 3-5 4-8Z" />
              </svg>
              HOT
            </span>
          )}
        </div>
      </div>
      <div className="p-4">
        <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">
          {lot.category} &middot; {lot.units_available.toLocaleString()} units
        </p>
        <h3 className="font-semibold text-white text-sm mb-3 leading-tight">
          {lot.title}
        </h3>

        {/* Location / Shipping / % off MSRP / MOQ / Cost, in one tidy row. */}
        <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-[11px] mb-3.5">
          {lot.location && <Chip d={PIN}>{lot.location}</Chip>}
          <Chip d={TRUCK}>{shipText}</Chip>
          {vsMsrp > 0 && (
            <Chip d={TAG} tone="text-green-400">
              {vsMsrp}% off MSRP
            </Chip>
          )}
          <Chip d={BOX}>MOQ {moq.toLocaleString()}</Chip>
          <Chip d={COIN}>{usd(lot.cost_price)}/unit</Chip>
        </div>

        {lot.status === "live" ? (
          <>
            {/* MOQ Price + Take All Price */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="rounded-xl bg-white/[0.03] border border-white/8 px-3 py-2">
                <p className="text-[9px] uppercase tracking-widest text-gray-500 mb-0.5">
                  MOQ &middot; {moq.toLocaleString()}
                </p>
                <p className="text-sm font-bold text-white tabular-nums">
                  {usd(moqPrice)}
                </p>
              </div>
              <div className="rounded-xl bg-blue-600/10 border border-blue-500/25 px-3 py-2">
                <p className="text-[9px] uppercase tracking-widest text-blue-400/80 mb-0.5">
                  Take All &middot; {lot.units_available.toLocaleString()}
                </p>
                <p className="text-sm font-bold text-blue-300 tabular-nums">
                  {usd(takeAllPrice)}
                </p>
              </div>
            </div>

          </>
        ) : (
          <span
            aria-disabled="true"
            className="mt-1 block text-center text-xs font-semibold text-gray-500 border border-white/5 rounded-lg py-2.5 cursor-not-allowed select-none"
          >
            {lot.status === "sold" ? "Sold Out" : "Coming Soon"}
          </span>
        )}
      </div>
    </motion.div>
  );
}

// Signed-in buyers get a "Picks for you" section: the three live lots that best
// match their onboarding preferences (the server already ordered `live` so the
// preferred categories lead), shown above the full "Live Inventory" grid.
export function PicksForYou({ lots }: { lots: Lot[] }) {
  const picks = lots.slice(0, 3);
  if (picks.length === 0) return null;
  return (
    <section className="pt-20 px-4 lg:px-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-1.5">
          Picks for you
        </h2>
        <p className="text-gray-400">
          Handpicked lots matched to your buying profile.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {picks.map((lot) => (
          <LotCard key={lot.id} lot={lot} />
        ))}
      </div>
    </section>
  );
}

// Signed-out visitors don't see the live inventory grid; in its place they get
// a static "Recently cleared" showcase: a few lots that have already sold out,
// rendered in the exact live-lot card layout. Every item is sold, so (like any
// sold LotCard) nothing here is clickable. Two of the three mirror the hero's
// fixed sold tiles (Nike Air Zoom, Jordan 1 Retro High); the Crocs lot is
// unique to this section.
const RECENTLY_CLEARED: Lot[] = [
  {
    id: "rc-nike-air-zoom",
    title: "Nike Air Zoom",
    category: "Sneakers",
    units_available: 320,
    min_order_units: 24,
    wholesale_price: 74,
    cost_price: 74,
    msrp: 150,
    image_url: "/hero/6aac5b8e9_generated_image.png",
    status: "sold",
    is_hot: false,
    created_date: "2026-07-28T00:00:00.000Z",
    freight_included: true,
    location: "Los Angeles, CA",
    ship_time: "Shipped in 3-5 days",
  },
  {
    id: "rc-crocs-classic-clog",
    title: "Crocs Classic Clog",
    category: "Footwear",
    units_available: 540,
    min_order_units: 36,
    wholesale_price: 21,
    cost_price: 21,
    msrp: 50,
    image_url: "/hero/crocs_classic_clog.jpg",
    status: "sold",
    is_hot: false,
    created_date: "2026-07-24T00:00:00.000Z",
    freight_included: true,
    location: "Dallas, TX",
    ship_time: "Shipped in 2-4 days",
  },
  {
    id: "rc-jordan-1-retro-high",
    title: "Jordan 1 Retro High",
    category: "Sneakers",
    units_available: 180,
    min_order_units: 12,
    wholesale_price: 88,
    cost_price: 88,
    msrp: 190,
    image_url: "/hero/30f2173b1_generated_image.png",
    status: "sold",
    is_hot: false,
    created_date: "2026-07-20T00:00:00.000Z",
    freight_included: false,
    location: "Atlanta, GA",
    ship_time: "Shipped in 4-6 days",
  },
];

export function RecentlyCleared() {
  return (
    <section className="py-20 px-4 lg:px-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <p className="text-xs font-bold tracking-widest uppercase text-red-500 mb-2">
          Sold out
        </p>
        <h2 className="text-3xl lg:text-4xl font-bold text-white">
          Recently cleared
        </h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {RECENTLY_CLEARED.map((lot) => (
          <LotCard key={lot.id} lot={lot} />
        ))}
      </div>
    </section>
  );
}

export function LotGrid({
  live = [],
  upcoming = [],
}: {
  live?: Lot[];
  upcoming?: Lot[];
}) {
  const [category, setCategory] = useState<string | null>(null);
  const combined = useMemo(() => [...live, ...upcoming], [live, upcoming]);

  const visible = useMemo(
    () =>
      category
        ? combined.filter((p) => p.category === category)
        : combined,
    [combined, category],
  );

  return (
    <section className="py-20 px-4 lg:px-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <p className="flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase text-red-500 mb-2">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-red-500" />
          </span>
          Live
        </p>
        <h2 className="text-3xl lg:text-4xl font-bold text-white">
          Inventory
        </h2>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {CATEGORY_FILTERS.map((c) => (
          <button
            key={c}
            onClick={() => setCategory((prev) => (prev === c ? null : c))}
            className={`px-3.5 py-1.5 text-xs font-medium rounded-full transition-colors ${
              category === c
                ? "bg-white text-black"
                : "bg-white/5 text-gray-400 hover:bg-white/10"
            }`}
          >
            {c}
          </button>
        ))}
      </div>
      <div className="mb-6" />
      {visible.length === 0 ? (
        <div className="text-center py-20 text-gray-500">
          <p className="mb-2">No lots match this filter yet.</p>
          <a href="/feed" className="text-blue-400 text-sm">
            Browse all inventory
          </a>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.slice(0, 9).map((h) => (
            <LotCard key={h.id} lot={h} />
          ))}
        </div>
      )}
    </section>
  );
}
